<template>
  <div class="chat-session-list" data-component-id="chat--session-list--root">
    <div class="csl-header">
      <span class="csl-title">
        <span class="csl-title-icon">📚</span>
        Riwayat Percakapan
        <span v-if="sessions.length" class="csl-count">{{ sessions.length }}</span>
      </span>
      <button class="csl-close" @click="$emit('close')" title="Tutup (Esc)" aria-label="Tutup riwayat">✕</button>
    </div>

    <div class="csl-toolbar">
      <button
        class="csl-new-btn"
        @click="$emit('new-session')"
        data-component-id="chat--session-list--btn-new"
      >
        <span class="csl-new-plus">＋</span>
        <span>Percakapan Baru</span>
      </button>
      <button
        class="csl-refresh"
        :disabled="isLoading"
        @click="$emit('refresh')"
        title="Muat ulang"
        aria-label="Muat ulang"
      >
        <span :class="['csl-refresh-icon', { 'csl-refresh-spin': isLoading }]">⟳</span>
      </button>
    </div>

    <!-- Carousel tab user — hanya muncul untuk super admin (props.users diisi).
         Yustian klik nama user lain → parent re-fetch sessions user tsb (read-only). -->
    <div v-if="users && users.length > 1" class="csl-user-tabs" role="tablist" aria-label="Pilih user">
      <button
        v-for="u in users"
        :key="u.userId"
        class="csl-user-tab"
        :class="{ 'csl-user-tab-active': u.userId === activeUserId, 'csl-user-tab-self': u.userId === selfUserId }"
        :title="`${u.userName || 'User '+u.userId} — ${u.questionCount || 0} pertanyaan, ${u.sessionCount || 0} sesi`"
        role="tab"
        :aria-selected="u.userId === activeUserId"
        @click="$emit('select-user', u)"
        data-component-id="chat--session-list--user-tab"
      >
        <span class="csl-user-tab-name">{{ u.userName || `User ${u.userId}` }}</span>
        <span v-if="u.userId === selfUserId" class="csl-user-tab-badge">You</span>
        <span v-else-if="u.questionCount" class="csl-user-tab-count">{{ u.questionCount }}</span>
      </button>
    </div>

    <div v-if="isReadOnly" class="csl-readonly-banner" role="status">
      <span class="csl-readonly-icon">👁</span>
      <span>Mode read-only — melihat riwayat <b>{{ readOnlyUserName || 'user lain' }}</b></span>
    </div>

    <div class="csl-search">
      <span class="csl-search-icon">🔍</span>
      <input
        v-model="search"
        type="search"
        class="csl-search-input"
        placeholder="Cari percakapan…"
        data-component-id="chat--session-list--search"
      />
      <button
        v-if="search"
        class="csl-search-clear"
        @click="search = ''"
        title="Hapus pencarian"
        aria-label="Hapus pencarian"
      >✕</button>
    </div>

    <div v-if="loadError" class="csl-error">
      <span>⚠️</span>
      <span>{{ loadError }}</span>
    </div>

    <div v-if="isLoading && !sessions.length" class="csl-empty">
      <div class="csl-spinner"></div>
      <div>Memuat...</div>
    </div>

    <div v-else-if="!filteredSessions.length" class="csl-empty">
      <div class="csl-empty-icon">{{ search ? '🔎' : '💭' }}</div>
      <div>{{ search ? 'Tidak ada hasil' : 'Belum ada percakapan' }}</div>
      <div class="csl-empty-hint">
        {{ search ? 'Coba kata kunci lain' : 'Mulai chat pertama Anda' }}
      </div>
    </div>

    <div v-else class="csl-items">
      <div
        v-for="s in filteredSessions"
        :key="s.sessionId"
        class="csl-item"
        :class="{ 'csl-item-active': s.sessionId === activeSessionId }"
        @click="$emit('select', s)"
      >
        <div class="csl-item-row">
          <span class="csl-item-title">{{ s.title || 'Percakapan baru' }}</span>
          <button
            v-if="!isReadOnly"
            class="csl-item-del"
            @click.stop="confirmDelete(s)"
            title="Hapus"
            aria-label="Hapus percakapan"
          >🗑</button>
        </div>
        <div class="csl-item-meta">
          <span class="csl-meta-time">{{ formatRelative(s.lastMessageAt) }}</span>
          <span class="csl-meta-sep">·</span>
          <span class="csl-meta-count">{{ questionLabel(s) }}</span>
          <span v-if="s.reportId" class="csl-meta-tag">{{ s.reportId }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  sessions:        { type: Array,  default: () => [] },
  isLoading:       { type: Boolean, default: false },
  loadError:       { type: String,  default: null },
  activeSessionId: { type: Number,  default: null },
  // Super-admin extras — semuanya optional, harmless untuk user biasa.
  users:           { type: Array,   default: () => [] },     // [{userId, userName, questionCount, sessionCount}]
  activeUserId:    { type: Number,  default: null },         // user yang sedang dilihat (default: self)
  selfUserId:      { type: Number,  default: null },         // userId Yustian, untuk badge "You"
  isReadOnly:      { type: Boolean, default: false },        // true saat lihat sesi user lain
  readOnlyUserName:{ type: String,  default: '' },
})

const emit = defineEmits(['select', 'delete', 'new-session', 'refresh', 'close', 'select-user'])

const search = ref('')

const filteredSessions = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return props.sessions
  return props.sessions.filter(s => {
    const title = (s.title || '').toLowerCase()
    const report = (s.reportId || '').toLowerCase()
    return title.includes(q) || report.includes(q)
  })
})

function confirmDelete(s) {
  if (!s) return
  if (props.isReadOnly) return
  if (window.confirm(`Hapus percakapan "${s.title || 'tanpa judul'}"?`)) {
    emit('delete', s)
  }
}

// Label "X pertanyaan" — pakai questionCount dari server (count Role='user').
// Fallback ke messageCount lama hanya jika field belum tersedia (sesi baru
// hasil fork/insert sebelum di-list ulang).
function questionLabel(s) {
  const qc = s?.questionCount
  const n = (qc != null && qc !== undefined) ? qc : (s?.messageCount || 0)
  return `${n} pertanyaan`
}

function formatRelative(value) {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const diff = Date.now() - date.getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'baru saja'
  if (min < 60) return `${min} mnt lalu`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} jam lalu`
  const d = Math.floor(hr / 24)
  if (d < 7) return `${d} hari lalu`
  return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
}
</script>

<style scoped>
.chat-session-list {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: var(--bg-base, var(--bg-deep));
  border-right: 1px solid var(--border-soft, var(--border-light));
  display: flex;
  flex-direction: column;
  animation: csl-slide-in 0.22s ease-out;
}
@keyframes csl-slide-in {
  from { transform: translateX(-12px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}

.csl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-soft, var(--border-light));
  background: var(--bg-elev, var(--bg-card));
}
.csl-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
}
.csl-title-icon { font-size: 14px; }
.csl-count {
  background: var(--accent-soft, rgba(59, 130, 246, 0.18));
  color: var(--accent-blue-light, var(--accent, #3b82f6));
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 9px;
  font-weight: 600;
  margin-left: 4px;
  min-width: 18px;
  text-align: center;
}
.csl-close {
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  width: 26px;
  height: 26px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.csl-close:hover {
  background: var(--bg-hover, var(--bg-hover));
  color: var(--accent-red, #ef4444);
}

.csl-toolbar {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
}
.csl-new-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: var(--accent, var(--accent-blue, #3b82f6));
  color: white;
  border: none;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px -2px var(--accent-soft, rgba(59, 130, 246, 0.4));
}
.csl-new-btn:hover {
  background: var(--accent-hover, var(--accent-blue, #2563eb));
  transform: translateY(-1px);
  box-shadow: 0 4px 12px -2px var(--accent-soft, rgba(59, 130, 246, 0.5));
}
.csl-new-btn:active { transform: scale(0.97); }
.csl-new-plus { font-size: 14px; line-height: 1; }
.csl-refresh {
  background: transparent;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  border-radius: 6px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.12s;
}
.csl-refresh:hover:not(:disabled) {
  background: var(--bg-hover, var(--bg-hover));
}
.csl-refresh:disabled { opacity: 0.5; cursor: not-allowed; }
.csl-refresh-icon { display: inline-block; }
.csl-refresh-spin { animation: csl-spin 0.9s linear infinite; }

/* ── Carousel tab user (super-admin) ──────────────────────────────────── */
.csl-user-tabs {
  display: flex;
  gap: 5px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 12px 8px;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}
.csl-user-tabs::-webkit-scrollbar { height: 4px; }
.csl-user-tabs::-webkit-scrollbar-thumb {
  background: var(--border-soft, var(--border-light));
  border-radius: 2px;
}
.csl-user-tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  padding: 4px 9px;
  border-radius: 14px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  white-space: nowrap;
  max-width: 160px;
}
.csl-user-tab:hover {
  background: var(--bg-hover, var(--bg-hover));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.csl-user-tab-active {
  background: var(--accent-soft, rgba(59, 130, 246, 0.16));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  color: var(--accent-blue-light, var(--accent, #3b82f6));
}
.csl-user-tab-name {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100px;
}
.csl-user-tab-badge {
  background: var(--accent, var(--accent-blue, #3b82f6));
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 7px;
  letter-spacing: 0.3px;
}
.csl-user-tab-count {
  background: var(--bg-base, var(--bg-deep));
  color: var(--text-dim, #888);
  font-size: 9.5px;
  padding: 1px 5px;
  border-radius: 7px;
  font-variant-numeric: tabular-nums;
}
.csl-user-tab-active .csl-user-tab-count {
  background: rgba(59, 130, 246, 0.25);
  color: var(--accent-blue-light, var(--accent, #3b82f6));
}

.csl-readonly-banner {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 12px 8px;
  padding: 7px 10px;
  background: rgba(234, 179, 8, 0.1);
  border: 1px solid rgba(234, 179, 8, 0.35);
  color: var(--accent-yellow, #eab308);
  font-size: 11px;
  border-radius: 5px;
}
.csl-readonly-banner b { color: var(--text-primary, var(--text-body)); }
.csl-readonly-icon { font-size: 12px; }

.csl-search {
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 12px 8px;
}
.csl-search-icon {
  position: absolute;
  left: 8px;
  font-size: 11px;
  color: var(--text-dim, #6b7a8d);
  pointer-events: none;
}
.csl-search-input {
  flex: 1;
  background: var(--bg-base, var(--bg-deep));
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  padding: 6px 28px 6px 26px;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}
.csl-search-input:focus {
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.csl-search-input::-webkit-search-cancel-button { display: none; }
.csl-search-clear {
  position: absolute;
  right: 4px;
  width: 22px;
  height: 22px;
  background: transparent;
  border: none;
  color: var(--text-dim, #6b7a8d);
  cursor: pointer;
  font-size: 11px;
  border-radius: 3px;
}
.csl-search-clear:hover {
  background: var(--bg-hover, var(--bg-hover));
  color: var(--text-base, var(--text-body));
}

.csl-error {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 4px 12px 8px;
  padding: 8px 10px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: var(--accent-red-light, #f87171);
  font-size: 11.5px;
  border-radius: 5px;
}

.csl-items {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.csl-item {
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  border-radius: 6px;
  padding: 9px 11px;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, transform 0.08s;
  position: relative;
}
.csl-item::before {
  content: '';
  position: absolute;
  left: -1px; top: 8px; bottom: 8px;
  width: 2px;
  background: transparent;
  border-radius: 2px;
  transition: background 0.15s;
}
.csl-item:hover {
  background: var(--bg-hover, var(--bg-hover));
  border-color: var(--accent, var(--accent-blue, #3b82f6));
}
.csl-item:hover::before { background: var(--accent, var(--accent-blue, #3b82f6)); }
.csl-item:active { transform: scale(0.99); }
.csl-item-active {
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  background: var(--accent-soft, rgba(59, 130, 246, 0.1));
}
.csl-item-active::before { background: var(--accent, var(--accent-blue, #3b82f6)); }

.csl-item-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 6px;
}
.csl-item-title {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text-primary, var(--text-body));
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.csl-item-del {
  background: transparent;
  border: none;
  color: var(--text-dim, #888);
  cursor: pointer;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 3px;
  opacity: 0;
  transition: opacity 0.15s, color 0.12s, background 0.12s;
}
.csl-item:hover .csl-item-del { opacity: 0.7; }
.csl-item-del:hover {
  color: var(--accent-red, #ef4444);
  background: rgba(239, 68, 68, 0.12);
  opacity: 1 !important;
}
.csl-item-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  color: var(--text-dim, #888);
  margin-top: 5px;
  flex-wrap: wrap;
}
.csl-meta-sep { opacity: 0.5; }
.csl-meta-tag {
  margin-left: auto;
  background: var(--bg-base, var(--bg-deep));
  border: 1px solid var(--border-soft, var(--border-light));
  padding: 1px 5px;
  border-radius: 3px;
  font-size: 9.5px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.csl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 6px;
  color: var(--text-dim, #888);
  font-size: 12px;
  padding: 32px 16px;
}
.csl-empty-icon { font-size: 32px; opacity: 0.7; }
.csl-empty-hint { font-size: 10.5px; opacity: 0.7; }

.csl-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid var(--border-soft, var(--border-light));
  border-top-color: var(--accent, var(--accent-blue, #3b82f6));
  border-radius: 50%;
  animation: csl-spin 0.9s linear infinite;
}
@keyframes csl-spin {
  to { transform: rotate(360deg); }
}
</style>
